import { ReactNode, useEffect, useState } from 'react';
import styles from './styles.scss';
import cn from 'classnames';
import { IconArrow } from 'components/Shared/Icon/themes/BGH-RD3/iconArrow';
import { NO_ELEMENT } from 'utils';
import { useAccordionContext } from './context';

interface AccordionSectionProps {
  title?: ReactNode;
  children?: ReactNode;
  isOpen?: boolean;
  setOpen?: () => void;
  hide?: boolean;
  right?: ReactNode;
  force?: 'open' | 'closed';
  contentContainerClassName?: string;
  defaultOpen?: boolean;
  id?: string;
  useScrollMargin?: boolean;
  disabled?: boolean;
  isReview?: boolean;
  sectionHeaderClassName?: string;
}

export const AccordionSection = ({
  title,
  children,
  hide,
  right,
  force,
  contentContainerClassName,
  id,
  useScrollMargin,
  disabled,
  defaultOpen,
  isReview,
  sectionHeaderClassName
}: AccordionSectionProps) => {
  const { shouldOpenReview } = useAccordionContext();
  const [isOpen, setIsOpen] = useState(defaultOpen);

  useEffect(() => {
    if (isReview && shouldOpenReview) {
      setIsOpen(shouldOpenReview);
    }
  }, [isReview, shouldOpenReview]);

  if (hide) {
    return NO_ELEMENT;
  }

  const onClick = () => !force && setIsOpen(!isOpen);

  return (
    <div
      id={id}
      className={cn(styles.container, {
        [styles.withScrollMargin]: useScrollMargin
      })}
    >
      {title && (
        <div
          onClick={onClick}
          className={cn(styles.sectionHeaderContainer, {
            [styles.fixed]: !!force,
            [styles.disabled]: disabled
          })}
        >
          <h4 className={cn(styles.sectionHeader, sectionHeaderClassName)}>{title}</h4>
          <span className={styles.rightSectionContainer}>
            {right}
            {!force && (
              <IconArrow
                className={styles.icon}
                appearance={isOpen ? 'flip-270' : 'flip-90'}
                size="tiny"
              />
            )}
          </span>
        </div>
      )}
      <div
        className={cn(
          styles.contentContainer,
          {
            [styles.space]: force === 'open' && isOpen,
            [styles.open]: force !== 'closed' && isOpen,
            [styles.close]: force !== 'open' && !isOpen
          },
          contentContainerClassName
        )}
      >
        <div className={styles.content}>{children}</div>
      </div>
    </div>
  );
};

interface AccordionContainerProps {
  children: ReactNode;
}

export const AccordionContainer = ({ children }: AccordionContainerProps) => {
  return <div className={styles.accordionContainer}>{children}</div>;
};
